import React, { useState } from 'react';
import { Check, Circle, KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CampoPassword } from './CampoPassword';
import { supabase } from '../../lib/supabase';
import { useNotifications } from '../../store/useStore';
import { cn } from '../../lib/cn';
import {
    nuovaPasswordValida,
    requisitiNuovaPassword,
    salvaNuovaPassword,
    verificaPasswordAttuale,
    type Requisito
} from '../../lib/account/cambioPassword';

/**
 * Cambio della propria password, in due passi: prima si conferma quella
 * attuale, poi si sceglie la nuova. Il perche' dei due passi e' spiegato in
 * lib/account/cambioPassword.ts.
 *
 * Le password stanno nella scheda solo finche' serve: si cancellano appena il
 * cambio riesce o si preme Annulla.
 */

type Passo = 'chiuso' | 'verifica' | 'nuova' | 'fatto';

interface PasswordCardProps {
    /** Email dell'account collegato: serve per ricontrollare la password attuale. */
    email: string;
}

export function PasswordCard({ email }: PasswordCardProps) {
    const [passo, setPasso] = useState<Passo>('chiuso');
    const [attuale, setAttuale] = useState('');
    const [nuova, setNuova] = useState('');
    const [conferma, setConferma] = useState('');
    const [errore, setErrore] = useState<string | null>(null);
    const [inCorso, setInCorso] = useState(false);
    const { addNotification } = useNotifications();

    const requisiti = requisitiNuovaPassword(nuova, conferma, attuale);

    const vaiA = (prossimo: Passo) => {
        setAttuale('');
        setNuova('');
        setConferma('');
        setErrore(null);
        setPasso(prossimo);
    };

    const confermaAttuale = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!attuale || inCorso) return;
        setInCorso(true);
        setErrore(null);
        const esito = await verificaPasswordAttuale(supabase.auth, email, attuale);
        setInCorso(false);
        if (esito) {
            setErrore(esito);
            return;
        }
        setPasso('nuova');
    };

    const salva = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!nuovaPasswordValida(requisiti) || inCorso) return;
        setInCorso(true);
        setErrore(null);
        const esito = await salvaNuovaPassword(supabase.auth, nuova);
        setInCorso(false);
        if (esito) {
            setErrore(esito);
            return;
        }
        addNotification({ type: 'success', title: 'Password cambiata' });
        vaiA('fatto');
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center gap-2">
                    <KeyRound className="h-5 w-5 text-primary-600" />
                    <CardTitle>Password</CardTitle>
                </div>
                <CardDescription className="break-words">
                    Quella con cui entri in Pixel come <span className="font-medium text-gray-700">{email}</span>.
                </CardDescription>
            </CardHeader>

            <CardContent>
                {passo === 'chiuso' && (
                    <Button variant="outline" onClick={() => vaiA('verifica')}>
                        Cambia password
                    </Button>
                )}

                {passo === 'verifica' && (
                    <form onSubmit={confermaAttuale} className="max-w-md space-y-4">
                        <TitoloPasso numero={1} titolo="Conferma che sei tu" />
                        <NomeUtenteNascosto email={email} />
                        <CampoPassword
                            etichetta="Password attuale"
                            valore={attuale}
                            onChange={setAttuale}
                            completamento="current-password"
                            disabilitato={inCorso}
                            conErrore={Boolean(errore)}
                            autoFocus
                        />
                        <MessaggioErrore testo={errore} />
                        <Pulsanti
                            testo="Avanti"
                            inCorso={inCorso}
                            disabilitato={!attuale}
                            onAnnulla={() => vaiA('chiuso')}
                        />
                    </form>
                )}

                {passo === 'nuova' && (
                    <form onSubmit={salva} className="max-w-md space-y-4">
                        <TitoloPasso numero={2} titolo="Scegli la nuova password" />
                        <NomeUtenteNascosto email={email} />
                        <CampoPassword
                            etichetta="Nuova password"
                            valore={nuova}
                            onChange={setNuova}
                            completamento="new-password"
                            disabilitato={inCorso}
                            autoFocus
                        />
                        <CampoPassword
                            etichetta="Riscrivila per conferma"
                            valore={conferma}
                            onChange={setConferma}
                            completamento="new-password"
                            disabilitato={inCorso}
                        />
                        <ElencoRequisiti requisiti={requisiti} />
                        <MessaggioErrore testo={errore} />
                        <Pulsanti
                            testo="Salva la nuova password"
                            inCorso={inCorso}
                            disabilitato={!nuovaPasswordValida(requisiti)}
                            onAnnulla={() => vaiA('chiuso')}
                        />
                    </form>
                )}

                {passo === 'fatto' && (
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start gap-2 text-emerald-700">
                            <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0" />
                            <p className="text-sm">
                                Password cambiata. Dalla prossima volta entri con quella nuova.
                            </p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => vaiA('chiuso')}>
                            Chiudi
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function TitoloPasso({ numero, titolo }: { numero: 1 | 2; titolo: string }) {
    return (
        <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Passo {numero} di 2</p>
            <p className="font-medium text-gray-900">{titolo}</p>
        </div>
    );
}

/**
 * I gestori di password (quello di Chrome, di Safari, 1Password...) capiscono
 * a quale account appartiene la password solo se nel modulo c'e' anche il nome
 * utente. Senza, non la riempiono al passo 1 e non la aggiornano al passo 2.
 */
function NomeUtenteNascosto({ email }: { email: string }) {
    return (
        <input
            type="email"
            name="username"
            autoComplete="username"
            value={email}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            className="sr-only"
        />
    );
}

function ElencoRequisiti({ requisiti }: { requisiti: readonly Requisito[] }) {
    return (
        <ul className="space-y-1 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm" aria-live="polite">
            {requisiti.map((r) => (
                <li key={r.chiave} className={cn('flex items-center gap-2', r.ok ? 'text-emerald-700' : 'text-gray-500')}>
                    {r.ok ? <Check className="h-4 w-4 flex-shrink-0" /> : <Circle className="h-4 w-4 flex-shrink-0" />}
                    <span>{r.testo}</span>
                    <span className="sr-only">{r.ok ? '(fatto)' : '(manca)'}</span>
                </li>
            ))}
        </ul>
    );
}

function MessaggioErrore({ testo }: { testo: string | null }) {
    if (!testo) return null;
    return (
        <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {testo}
        </p>
    );
}

interface PulsantiProps {
    testo: string;
    inCorso: boolean;
    disabilitato: boolean;
    onAnnulla: () => void;
}

function Pulsanti({ testo, inCorso, disabilitato, onAnnulla }: PulsantiProps) {
    return (
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button type="button" variant="outline" onClick={onAnnulla} disabled={inCorso}>
                Annulla
            </Button>
            <Button type="submit" disabled={disabilitato || inCorso}>
                {inCorso && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {testo}
            </Button>
        </div>
    );
}

export default PasswordCard;
